/**
 * Add or edit one routing rule (IT management). People are typed as names,
 * lead first; the server looks them up in ITSM. A name that did not change
 * keeps the email and RecId already found for it.
 */

import { Button, Checkbox, Dialog, Field, Input, Textarea } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState } from "@/components/common";
import { OptionSelect } from "@/features/knowledge/shared";
import { useT } from "@/i18n";
import type { RoutingRule } from "@/lib/api";

import { useSaveRule } from "../assign/api";
import { namesToPeople, splitList } from "../assign/lib";

type Kind = "incident" | "request" | "any";

export function RuleDialog({
  rule,
  open,
  onOpenChange,
}: {
  rule: RoutingRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useT("tickets");
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="lg" className="flex max-h-[85vh] w-full flex-col">
          <Dialog.Header>
            <Dialog.Title>
              {rule ? t("itAdmin.routing.edit") : t("itAdmin.routing.add")}
            </Dialog.Title>
            <Dialog.CloseButton label={t("itAdmin.routing.cancel")} />
          </Dialog.Header>
          {open ? <RuleForm rule={rule} onDone={() => onOpenChange(false)} /> : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function RuleForm({ rule, onDone }: { rule: RoutingRule | null; onDone: () => void }) {
  const { t } = useT("tickets");
  const save = useSaveRule();
  const [label, setLabel] = useState(rule?.label ?? "");
  const [kind, setKind] = useState<Kind>(rule?.kind ?? "any");
  const [service, setService] = useState(rule?.service ?? "");
  const [category, setCategory] = useState(rule?.category ?? "");
  const [offering, setOffering] = useState(rule?.offering ?? "");
  const [keywords, setKeywords] = useState((rule?.keywords ?? []).join(", "));
  const [team, setTeam] = useState(rule?.team ?? "");
  const [owners, setOwners] = useState((rule?.owners ?? []).map((p) => p.name).join(", "));
  const [backups, setBackups] = useState((rule?.backups ?? []).map((p) => p.name).join(", "));
  const [note, setNote] = useState(rule?.note ?? "");
  const [confirmed, setConfirmed] = useState(rule?.confirmed ?? true);
  const [reason, setReason] = useState("");
  const ready = label.trim() !== "" && team.trim() !== "";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) {
      return;
    }
    save.mutate(
      {
        id: rule?.id ?? null,
        body: {
          area_key: rule?.area_key ?? "",
          label: label.trim(),
          kind,
          service: service.trim(),
          category: category.trim(),
          offering: offering.trim(),
          keywords: splitList(keywords),
          team: team.trim(),
          owners: namesToPeople(owners, rule?.owners ?? []),
          backups: namesToPeople(backups, rule?.backups ?? []),
          note: note.trim() === "" ? null : note.trim(),
          confirmed,
          reason: reason.trim() === "" ? null : reason.trim(),
        },
      },
      { onSuccess: onDone },
    );
  }

  const kinds: Kind[] = ["incident", "request", "any"];
  return (
    <form onSubmit={submit} className="contents">
      <Dialog.Body className="flex flex-col gap-3 overflow-y-auto">
        {save.error == null ? null : <ErrorState error={save.error} variant="inline" />}
        <Field name="rule-label" label={t("itAdmin.routing.field.label")} required>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={255} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field name="rule-kind" label={t("itAdmin.routing.field.kind")}>
            <OptionSelect
              options={kinds.map((k) => ({ value: k, label: t(`itAdmin.routing.kind.${k}`) }))}
              value={kind}
              onValueChange={(v) => setKind((v || "any") as Kind)}
              placeholder={t("itAdmin.routing.kind.any")}
              aria-label={t("itAdmin.routing.field.kind")}
            />
          </Field>
          <Field name="rule-team" label={t("itAdmin.routing.field.team")} required>
            <Input value={team} onChange={(e) => setTeam(e.target.value)} maxLength={128} />
          </Field>
          <Field name="rule-service" label={t("itAdmin.routing.field.service")}>
            <Input value={service} onChange={(e) => setService(e.target.value)} maxLength={255} />
          </Field>
          <Field name="rule-category" label={t("itAdmin.routing.field.category")}>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={255} />
          </Field>
        </div>
        <Field name="rule-offering" label={t("itAdmin.routing.field.offering")}>
          <Input value={offering} onChange={(e) => setOffering(e.target.value)} maxLength={255} />
        </Field>
        <Field
          name="rule-keywords"
          label={t("itAdmin.routing.field.keywords")}
          hint={t("itAdmin.routing.field.keywordsHint")}
        >
          <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={1000} />
        </Field>
        <Field
          name="rule-owners"
          label={t("itAdmin.routing.field.owners")}
          hint={t("itAdmin.routing.field.ownersHint")}
        >
          <Input value={owners} onChange={(e) => setOwners(e.target.value)} maxLength={1000} />
        </Field>
        <Field name="rule-backups" label={t("itAdmin.routing.field.backups")}>
          <Input value={backups} onChange={(e) => setBackups(e.target.value)} maxLength={1000} />
        </Field>
        <Field name="rule-note" label={t("itAdmin.routing.field.note")}>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            autoResize
            maxLength={2000}
          />
        </Field>
        <Checkbox.Root
          checked={confirmed}
          onCheckedChange={(next) => setConfirmed(next === true)}
          label={t("itAdmin.routing.field.confirmed")}
        />
        <Field name="rule-reason" label={t("itAdmin.routing.field.reason")}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Close render={<Button variant="outline" tone="neutral" type="button" />}>
          {t("itAdmin.routing.cancel")}
        </Dialog.Close>
        <Button type="submit" loading={save.isPending} disabled={!ready}>
          {t("itAdmin.routing.save")}
        </Button>
      </Dialog.Footer>
    </form>
  );
}
